import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const SocialContext = createContext(null);

export const useSocial = () => {
  const context = useContext(SocialContext);
  if (!context) {
    throw new Error('useSocial must be used within SocialProvider');
  }
  return context;
};

export const SocialProvider = ({ children }) => {
  const { api } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get('/social/accounts');
      setAccounts(res.data);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [api]);

  const connectAccount = useCallback(async (accountData) => {
    const res = await api().post('/social/accounts', accountData);
    setAccounts(prev => {
      const existing = prev.findIndex(a => a.id === res.data.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = res.data;
        return updated;
      }
      return [...prev, res.data];
    });
    return res.data;
  }, [api]);

  const disconnectAccount = useCallback(async (accountId) => {
    await api().delete(`/social/accounts/${accountId}`);
    setAccounts(prev => prev.filter(a => a.id !== accountId));
  }, [api]);

  const fetchScheduledPosts = useCallback(async () => {
    const res = await api().get('/social/posts');
    setScheduledPosts(res.data);
    return res.data;
  }, [api]);

  const schedulePost = useCallback(async (postData) => {
    const res = await api().post('/social/posts', postData);
    setScheduledPosts(prev => [...prev, res.data]);
    return res.data;
  }, [api]);

  const cancelPost = useCallback(async (postId) => {
    await api().delete(`/social/posts/${postId}`);
    setScheduledPosts(prev => prev.filter(p => p.id !== postId));
  }, [api]);

  return (
    <SocialContext.Provider value={{
      accounts,
      scheduledPosts,
      loading,
      fetchAccounts,
      connectAccount,
      disconnectAccount,
      fetchScheduledPosts,
      schedulePost,
      cancelPost
    }}>
      {children}
    </SocialContext.Provider>
  );
};
